import { Eye, EyeOff, Loader2, RefreshCw, Search, WandSparkles } from "lucide-react";
import { useState, type KeyboardEvent } from "react";

import {
  DateRangeFilter,
  type DateRangePreset,
} from "@/components/inbox/date-range-filter";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/ui/multi-select";
import type { SourceConfig } from "@/lib/api";
import { dotClass, sourceColor } from "@/lib/source-color";
import { cn } from "@/lib/utils";

export interface InboxFilters {
  preset: DateRangePreset;
  timeBasis: "captured" | "source";
  sourceIds: string[];
  itemTypes: string[];
  symbol?: string;
  /** false = hide archived + ignore (the default). */
  showHidden: boolean;
}

interface Props {
  sources: SourceConfig[];
  filters: InboxFilters;
  onChange: (next: InboxFilters) => void;
  onAnalyze: () => void;
  onRefresh: () => void;
  analyzeTitle?: string;
  analyzeCount?: number;
  isAnalyzing?: boolean;
  isRefreshing?: boolean;
}

const ITEM_TYPE_OPTIONS = [
  { value: "trade_alert", label: "Trade alert" },
  { value: "watchlist", label: "Watchlist" },
  { value: "announcement", label: "Announcement" },
  { value: "info", label: "Info" },
  { value: "correction", label: "Correction" },
  { value: "vendor_model_portfolio", label: "Model portfolio" },
  { value: "marketing", label: "Marketing" },
  { value: "live_class", label: "Live class" },
  { value: "unknown", label: "Unknown" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
];

export function InboxFilterBar({
  sources,
  filters,
  onChange,
  onAnalyze,
  onRefresh,
  analyzeTitle,
  analyzeCount,
  isAnalyzing,
  isRefreshing,
}: Props) {
  const [symbolDraft, setSymbolDraft] = useState(filters.symbol ?? "");

  const commitSymbol = () => {
    const cleaned = symbolDraft.trim().toUpperCase();
    if ((cleaned || undefined) !== filters.symbol) {
      onChange({ ...filters, symbol: cleaned || undefined });
    }
  };

  const sourceOptions = sources.map((s) => ({
    value: s.id,
    label: s.display_name,
    dotClass: dotClass(sourceColor(s.id)),
  }));

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      <DateRangeFilter
        value={filters.preset}
        onChange={(preset) => onChange({ ...filters, preset })}
      />
      <TimeBasisFilter
        value={filters.timeBasis}
        onChange={(timeBasis) => onChange({ ...filters, timeBasis })}
      />

      <div className="flex flex-wrap items-center gap-2">
        <MultiSelect
          label="Sources"
          options={sourceOptions}
          value={filters.sourceIds}
          onChange={(sourceIds) => onChange({ ...filters, sourceIds })}
          triggerClassName="w-[180px]"
        />

        <MultiSelect
          label="Types"
          options={ITEM_TYPE_OPTIONS}
          value={filters.itemTypes}
          onChange={(itemTypes) => onChange({ ...filters, itemTypes })}
          triggerClassName="w-[160px]"
        />

        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-fg" />
          <input
            type="text"
            placeholder="Symbol"
            value={symbolDraft}
            onChange={(e) => setSymbolDraft(e.target.value)}
            onBlur={commitSymbol}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") commitSymbol();
            }}
            className="h-9 w-[120px] rounded-md border border-border bg-transparent pl-8 pr-2 text-sm placeholder:text-muted-fg focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 sm:ml-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={onAnalyze}
          disabled={isAnalyzing || analyzeCount === 0}
          title={analyzeTitle ?? "Analyze matching inbox items"}
        >
          {isAnalyzing ? (
            <Loader2 className="animate-spin" />
          ) : (
            <WandSparkles />
          )}
          <span>
            Analyze{analyzeCount && analyzeCount > 0 ? ` ${analyzeCount}` : ""}
          </span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({ ...filters, showHidden: !filters.showHidden })
          }
          aria-pressed={filters.showHidden}
          title={
            filters.showHidden
              ? "Hide archived + ignored"
              : "Show archived + ignored"
          }
          className={cn(
            filters.showHidden && "bg-muted/60 text-fg",
          )}
        >
          {filters.showHidden ? <Eye /> : <EyeOff />}
          <span className="hidden md:inline">
            {filters.showHidden ? "Hiding off" : "Hide archived"}
          </span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label="Refresh"
        >
          <RefreshCw
            className={isRefreshing ? "animate-spin" : undefined}
            aria-hidden
          />
          <span className="hidden md:inline">Refresh</span>
        </Button>
      </div>
    </div>
  );
}

function TimeBasisFilter({
  value,
  onChange,
}: {
  value: "captured" | "source";
  onChange: (value: "captured" | "source") => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Date mode"
      className="inline-flex h-9 items-center rounded-md border border-border bg-transparent p-0.5 text-sm"
    >
      {[
        { value: "captured" as const, label: "Captured" },
        { value: "source" as const, label: "Source" },
      ].map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => onChange(option.value)}
            title={
              option.value === "captured"
                ? "Filter by when Signals captured the item"
                : "Filter by the vendor alert time"
            }
            className={cn(
              "h-full rounded px-3 text-xs font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-accent",
              active
                ? "bg-accent/20 text-accent ring-1 ring-accent/40"
                : "text-muted-fg hover:bg-muted/60 hover:text-fg",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
